import React, { useState, useEffect, useCallback } from 'react';
import {
    Search, ChevronLeft, ChevronRight, User, Calendar,
    CheckCircle2, Clock, AlertCircle, Eye, Download, Loader2,
    UserX, Mail, Briefcase
} from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const StatusBadge = ({ status }) => {
    const config = {
        completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle2, label: 'Completed' },
        in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Clock, label: 'In Progress' },
        pending: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', icon: AlertCircle, label: 'Pending' },
        declined: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: UserX, label: 'Declined' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
            <Icon size={12} />
            {c.label}
        </span>
    );
};

const IndividualResponsesView = ({ surveyId, onViewDetail, isAnonymous }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const limit = 15;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit, search });
            const response = await api.get(`/surveys/${surveyId}/analytics/individual?${params}`);
            setData(response.data);
        } catch (error) {
            console.error('Error fetching individual responses:', error);
            toast.error('Failed to load individual responses');
        } finally {
            setLoading(false);
        }
    }, [surveyId, page, search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleExport = async () => {
        try {
            const response = await api.get(`/surveys/${surveyId}/export`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const date = new Date().toISOString().slice(0, 7);
            link.setAttribute('download', `Survey_Individual_Responses_${date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Individual responses exported!');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export failed');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    const getAnswerDisplay = (answers, questionId, questionType) => {
        const answer = answers?.[questionId];
        if (!answer) return <span className="text-gray-300">—</span>;
        if (answer.skipped) return <span className="text-gray-400 italic text-xs">Skipped</span>;

        if (questionType === 'rating') {
            const val = answer.response_value || parseInt(answer.response_text);
            return (
                <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-800">{val}</span>
                    <span className="text-xs text-gray-400">/5</span>
                </div>
            );
        }
        if (questionType === 'boolean') {
            const val = (answer.response_text || '').toLowerCase();
            const isYes = ['yes', 'true', '1'].includes(val);
            return (
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${isYes ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {isYes ? 'Yes' : 'No'}
                </span>
            );
        }
        // text or multiple_choice
        const text = answer.response_text || '';
        return (
            <span className="text-sm text-gray-700 truncate max-w-[200px] block" title={text}>
                {text.length > 40 ? text.substring(0, 40) + '…' : text}
            </span>
        );
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 size={28} className="animate-spin text-blue-600" />
            </div>
        );
    }

    if (!data) return null;

    const { employees, total, total_pages, questions } = data;
    // Show max 3 question columns in the table for readability
    const visibleQuestions = questions.slice(0, 3);
    const hasMoreQuestions = questions.length > 3;

    return (
        <div className="space-y-4">
            {/* Response Rate Banner */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-800">
                            {total} employee{total !== 1 ? 's' : ''} assigned
                        </p>
                        <p className="text-xs text-gray-500">
                            {employees.filter(e => e.status === 'completed').length} completed · {employees.filter(e => e.status === 'in_progress').length} in progress · {employees.filter(e => e.status === 'pending').length} pending
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={14} />
                    Export
                </button>
            </div>

            {/* Search */}
            {!isAnonymous && (
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search by employee name or email..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-colors"
                    />
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {loading && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-xl">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                )}
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    {isAnonymous ? 'Respondent' : 'Employee'}
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                                {visibleQuestions.map(q => (
                                    <th key={q.id} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide max-w-[180px]">
                                        <span className="truncate block" title={q.question_text}>
                                            {q.question_text.length > 25 ? q.question_text.substring(0, 25) + '…' : q.question_text}
                                        </span>
                                    </th>
                                ))}
                                {hasMoreQuestions && (
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                        +{questions.length - 3} more
                                    </th>
                                )}
                                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {employees.length === 0 ? (
                                <tr>
                                    <td colSpan={4 + visibleQuestions.length + (hasMoreQuestions ? 1 : 0)} className="px-4 py-12 text-center text-gray-500">
                                        {search ? 'No matching responses found' : 'No responses yet'}
                                    </td>
                                </tr>
                            ) : (
                                employees.map((emp, idx) => (
                                    <tr
                                        key={emp.instance_id}
                                        className="hover:bg-gray-50/60 cursor-pointer transition-colors"
                                        onClick={() => onViewDetail?.(emp.instance_id)}
                                    >
                                        <td className="px-4 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                                    {isAnonymous ? '#' : (emp.employee_name?.charAt(0) || '?')}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">{emp.employee_name}</p>
                                                    {!isAnonymous && emp.email && (
                                                        <p className="text-xs text-gray-400 truncate">{emp.email}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <StatusBadge status={emp.status} />
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-gray-600">
                                            {formatDate(emp.completed_at || emp.deployed_at)}
                                        </td>
                                        {visibleQuestions.map(q => (
                                            <td key={q.id} className="px-4 py-3.5">
                                                {getAnswerDisplay(emp.answers, q.id, q.question_type)}
                                            </td>
                                        ))}
                                        {hasMoreQuestions && (
                                            <td className="px-4 py-3.5 text-xs text-gray-400">…</td>
                                        )}
                                        <td className="px-4 py-3.5 text-right">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onViewDetail?.(emp.instance_id);
                                                }}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                            >
                                                <Eye size={12} />
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total_pages > 1 && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
                        </p>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-white hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-xs text-gray-600 px-2">
                                Page {page} of {total_pages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(total_pages, p + 1))}
                                disabled={page >= total_pages}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-white hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndividualResponsesView;
